import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Badge } from '../../../ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Edit, 
  Star, 
  MapPin, 
  Clock, 
  Phone, 
  Mail, 
  User, 
  Award,
  MessageSquare,
  Calendar,
  DollarSign
} from 'lucide-react';
import { doctorService } from '../../../../services';

const AdminDoctorView = () => {
  const { doctorId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [doctor, setDoctor] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [availability, setAvailability] = useState(null);

  useEffect(() => {
    const loadDoctorData = async () => {
      try {
        setLoading(true);
        
        // Load doctor data
        const doctorData = await doctorService.getDoctorById(doctorId);
        setDoctor(doctorData);
        
        // Load additional data in parallel
        try {
          const [reviewsData, availabilityData] = await Promise.all([
            doctorService.getDoctorReviews(doctorId),
            doctorService.getDoctorAvailability(doctorId)
          ]);
          
          setReviews(reviewsData?.reviews || []);
          setAvailability(availabilityData);
        } catch (error) {
          console.warn('Could not load additional doctor data:', error);
        }
        
      } catch (error) {
        console.error('Error loading doctor data:', error);
        toast.error('Failed to load doctor data');
        navigate('/admin/doctors');
      } finally {
        setLoading(false);
      }
    };

    if (doctorId) {
      loadDoctorData();
    }
  }, [doctorId, navigate]);

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading doctor data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!doctor) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Doctor Not Found</h1>
          <Button onClick={() => navigate('/admin/doctors')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Doctors
          </Button>
        </div>
      </div>
    );
  }

  // Derived safe fields for display
  const displayName = doctor.fullName || doctor.name || 'Doctor';
  const qualificationsList = Array.isArray(doctor.qualifications)
    ? doctor.qualifications.map((q) => {
        if (typeof q === 'string') return q;
        if (q && typeof q === 'object') {
          return q.degree || '';
        }
        return '';
      }).filter(Boolean)
    : [];

  return (
    <div className="container mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/doctors')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Doctors
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Doctor Details</h1>
            <p className="text-muted-foreground">View complete doctor information</p>
          </div>
        </div>
        <Button onClick={() => navigate(`/admin/doctors/${doctorId}/edit`)}>
          <Edit className="h-4 w-4 mr-2" />
          Edit Doctor
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <User className="h-5 w-5" />
                <span>Basic Information</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-start space-x-4">
                {doctor.profileImageUrl && (
                  <img
                    src={doctor.profileImageUrl}
                    alt={displayName}
                    className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
                  />
                )}
                <div className="flex-1">
                  <h2 className="text-2xl font-bold">
                    {doctor.professionalTitle || 'Dr.'} {displayName}
                  </h2>
                  <p className="text-lg text-muted-foreground">{doctor.mainCategory || doctor.specialty || 'Not specified'}</p>
                  <div className="flex items-center space-x-2 mt-2">
                    <Star className="h-4 w-4 text-yellow-500" />
                    <span className="font-medium">{doctor.rating || 'N/A'}</span>
                    <span className="text-muted-foreground">({doctor.reviewCount || reviews.length} reviews)</span>
                  </div>
                  {doctor.employeeCode && (
                    <div className="text-sm text-muted-foreground mt-1">
                      Employee Code: {doctor.employeeCode}
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                  <Award className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Experience:</span>
                  <span>{doctor.experience || 'Not specified'}</span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Consultation Fee:</span>
                  <span>₹{doctor.consultationFees || 'Not specified'}</span>
                </div>

                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Contact:</span>
                  <span>{doctor.contactNumber || 'Not specified'}</span>
                </div>
              </div>

              <div>
                <span className="font-medium">Qualifications:</span>
                {qualificationsList.length > 0 ? (
                  <ul className="list-disc pl-5 text-muted-foreground mt-1">
                    {qualificationsList.map((q, idx) => (
                      <li key={idx}>{q}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted-foreground">Not specified</p>
                )}
              </div>

              {doctor.about && (
                <div>
                  <span className="font-medium">About:</span>
                  <p className="text-muted-foreground mt-1">{doctor.about}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Specialties & Cities */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Specialty</CardTitle>
              </CardHeader>
              <CardContent>
                <Badge variant="secondary" className="text-sm">
                  {doctor.specialty || 'Not specified'}
                </Badge>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <MapPin className="h-4 w-4" />
                  <span>Available Cities</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {doctor.cities && doctor.cities.length > 0 ? (
                    doctor.cities.map((city, index) => (
                      <Badge key={index} variant="outline">
                        {city}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-muted-foreground">No cities specified</span>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>


          {/* Doctor Type */}
          <Card>
            <CardHeader>
              <CardTitle>Doctor Type</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {doctor.isVisitingDoctor && (
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                    Visiting Doctor
                  </Badge>
                )}
                {doctor.isHospitalDoctor && (
                  <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                    Hospital Doctor
                  </Badge>
                )}
                {!doctor.isVisitingDoctor && !doctor.isHospitalDoctor && (
                  <span className="text-muted-foreground">Not specified</span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Languages */}
          <Card>
            <CardHeader>
              <CardTitle>Languages</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {doctor.languages && doctor.languages.length > 0 ? (
                  doctor.languages.map((language, index) => (
                    <Badge key={index} variant="outline">
                      {language}
                    </Badge>
                  ))
                ) : (
                  <span className="text-muted-foreground">No languages specified</span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Reviews */}
          {reviews.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <MessageSquare className="h-5 w-5" />
                  <span>Recent Reviews</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {reviews.slice(0, 3).map((review, index) => (
                  <div key={index} className="border-l-4 border-blue-500 pl-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Star className="h-4 w-4 text-yellow-500" />
                      <span className="font-medium">{review.rating}/5</span>
                      <span className="text-muted-foreground">by {review.patientName}</span>
                    </div>
                    <p className="text-sm text-muted-foreground">{review.comment}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(review.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Availability */}
          {availability && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Clock className="h-5 w-5" />
                  <span>Availability</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {availability.workingHours && availability.workingHours.length > 0 ? (
                  <div className="space-y-2">
                    {availability.workingHours.map((schedule, index) => (
                      <div key={index} className="flex justify-between text-sm">
                        <span className="font-medium">{schedule.day}</span>
                        <span className="text-muted-foreground">
                          {schedule.startTime} - {schedule.endTime}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">No availability information</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Quick Stats */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Stats</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Total Reviews</span>
                <span className="font-medium">{doctor.reviewCount || reviews.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Average Rating</span>
                <span className="font-medium">{doctor.rating || 'N/A'}/5</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Cities</span>
                <span className="font-medium">{doctor.cities?.length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Languages</span>
                <span className="font-medium">{doctor.languages?.length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <Badge variant={doctor.status === 'active' ? 'default' : 'destructive'}>
                  {doctor.status?.toUpperCase() || 'N/A'}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button 
                className="w-full" 
                onClick={() => navigate(`/admin/doctors/${doctorId}/edit`)}
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Doctor
              </Button>
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => navigate('/admin/doctors')}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to List
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdminDoctorView;